import type { DrawingToolType, State } from "./types";
import type { IndicatorConfig } from "./indicators";
import type { DrawingObject } from "./types";

export type PersistenceScope = {
  userId?: string;
  authToken?: string;
  symbol?: string;
};

type PersistedChartSetup = {
  version: 1;
  indicatorType: State["indicatorType"];
  indicatorPeriod: number;
  indicatorColor: string;
  indicatorLineWidth: number;
  indicators: IndicatorConfig[];
  activeDrawingTool: DrawingToolType | null;
  drawings: DrawingObject[];
};

type PersistenceContext = {
  scope: PersistenceScope;
  timer: number | null;
};

const STORAGE_PREFIX = "chart-learning:setup";
const contexts = new WeakMap<State, PersistenceContext>();

function sanitizeSegment(value: string): string {
  return value.trim().toLowerCase().replace(/[^a-z0-9._-]+/g, "_").slice(0, 120) || "unknown";
}

function hashString(input: string): string {
  let hash = 2166136261;
  for (let i = 0; i < input.length; i++) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(36);
}

function decodeJwtSubject(token: string): string | null {
  const parts = token.split(".");
  if (parts.length < 2) return null;

  try {
    const payload = JSON.parse(
      atob(parts[1].replace(/-/g, "+").replace(/_/g, "/")),
    ) as Record<string, unknown>;
    const subject =
      (typeof payload.sub === "string" && payload.sub) ||
      (typeof payload.user_id === "string" && payload.user_id) ||
      (typeof payload.userId === "string" && payload.userId) ||
      (typeof payload.email === "string" && payload.email) ||
      (typeof payload.id === "string" && payload.id) ||
      (typeof payload.preferred_username === "string" && payload.preferred_username) ||
      null;
    return subject ? subject : null;
  } catch {
    return null;
  }
}

function resolveUserKey(scope: PersistenceScope): string {
  if (scope.userId && scope.userId.trim()) return `user:${sanitizeSegment(scope.userId)}`;

  if (scope.authToken && scope.authToken.trim()) {
    const jwtSubject = decodeJwtSubject(scope.authToken);
    if (jwtSubject) return `user:${sanitizeSegment(jwtSubject)}`;
    return `token:${hashString(scope.authToken)}`;
  }

  const fallbackToken = window.localStorage.getItem("chart-main-auth-token") ?? "";
  if (fallbackToken) {
    const jwtSubject = decodeJwtSubject(fallbackToken);
    if (jwtSubject) return `user:${sanitizeSegment(jwtSubject)}`;
    return `token:${hashString(fallbackToken)}`;
  }

  return "anonymous";
}

function getStorageKey(scope: PersistenceScope): string {
  const userKey = resolveUserKey(scope);
  const symbolKey = scope.symbol && scope.symbol.trim() ? sanitizeSegment(scope.symbol) : "global";
  return `${STORAGE_PREFIX}:${userKey}:${symbolKey}`;
}

function cloneIndicators(indicators: IndicatorConfig[]): IndicatorConfig[] {
  return indicators.map((indicator) => ({
    ...indicator,
    params: { ...((indicator as IndicatorConfig & { params?: Record<string, unknown> }).params ?? {}) },
  }));
}

function cloneDrawings(drawings: DrawingObject[]): DrawingObject[] {
  return drawings.map((drawing) => ({
    ...drawing,
    points: drawing.points.map((point) => ({ ...point })),
  }));
}

export function bindChartPersistence(state: State, scope: PersistenceScope): void {
  contexts.set(state, { scope, timer: null });
}

export function restoreChartPersistence(state: State): boolean {
  const context = contexts.get(state);
  if (!context) return false;

  const storageKey = getStorageKey(context.scope);
  const raw = window.localStorage.getItem(storageKey);
  if (!raw) return false;

  try {
    const parsed = JSON.parse(raw) as Partial<PersistedChartSetup> & { version?: number };
    if (parsed.version !== 1) return false;

    state.indicatorType = parsed.indicatorType ?? state.indicatorType;
    state.indicatorPeriod = typeof parsed.indicatorPeriod === "number" ? parsed.indicatorPeriod : state.indicatorPeriod;
    state.indicatorColor = typeof parsed.indicatorColor === "string" ? parsed.indicatorColor : state.indicatorColor;
    state.indicatorLineWidth = typeof parsed.indicatorLineWidth === "number" ? parsed.indicatorLineWidth : state.indicatorLineWidth;
    state.indicators = Array.isArray(parsed.indicators) ? cloneIndicators(parsed.indicators) : state.indicators;
    state.activeDrawingTool = parsed.activeDrawingTool ?? null;
    state.drawings = Array.isArray(parsed.drawings) ? cloneDrawings(parsed.drawings) : state.drawings;

    if (state.selectedDrawingId && !state.drawings.some((drawing) => drawing.id === state.selectedDrawingId)) {
      state.selectedDrawingId = null;
    }
    if (state.hoveredDrawingId && !state.drawings.some((drawing) => drawing.id === state.hoveredDrawingId)) {
      state.hoveredDrawingId = null;
    }
    if (state.draggingDrawing && !state.drawings.some((drawing) => drawing.id === state.draggingDrawing?.id)) {
      state.draggingDrawing = null;
    }
    return true;
  } catch {
    return false;
  }
}

export function scheduleChartPersistence(state: State): void {
  const context = contexts.get(state);
  if (!context) return;

  if (context.timer !== null) {
    window.clearTimeout(context.timer);
  }

  context.timer = window.setTimeout(() => {
    context.timer = null;
    const storageKey = getStorageKey(context.scope);
    const payload: PersistedChartSetup = {
      version: 1,
      indicatorType: state.indicatorType,
      indicatorPeriod: state.indicatorPeriod,
      indicatorColor: state.indicatorColor,
      indicatorLineWidth: state.indicatorLineWidth,
      indicators: cloneIndicators(state.indicators),
      activeDrawingTool: state.activeDrawingTool,
      drawings: cloneDrawings(state.drawings),
    };
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(payload));
    } catch {
      // Ignore storage quota / privacy mode failures.
    }
  }, 150);
}

export function clearChartPersistence(state: State): void {
  const context = contexts.get(state);
  if (!context) return;
  const storageKey = getStorageKey(context.scope);
  window.localStorage.removeItem(storageKey);
}
